import { useState } from "react";
import { Clock, Mail, MapPin, MessageCircle, Phone, Send } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Overline, Reveal } from "@/components/Reveal";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { COMPANY, WHATSAPP_LINK_SUSHANT } from "@/data/site";
import { trackEvent } from "@/lib/analytics";

const PRODUCT_OPTIONS = [
    "Rapeseed/Mustard De-Oiled Cake (DOC)",
    "Groundnut De-Oiled Cake",
    "Sesame Oil Cake",
    "Soybean De-Oiled Cake / Soy Meal",
    "Other / Multiple Products",
];

const TERMS_OPTIONS = ["FOB Indian Port", "FOB Mundra", "FOB Kandla", "FOB JNPT", "CFR", "CIF", "Not Sure – Negotiable"];

const initialForm = {
    fullName: "",
    company: "",
    email: "",
    phone: "",
    country: "",
    port: "",
    product: PRODUCT_OPTIONS[0],
    grade: "",
    quantity: "",
    terms: TERMS_OPTIONS[0],
    targetMonth: "",
    message: "",
    coa: false,
    consent: false,
};

const inputClass =
    "w-full rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm text-navy outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-slate-400 focus:border-steel focus:ring-2 focus:ring-steel/25";

const Field = ({ label, htmlFor, required, children, testid }) => (
    <div>
        <label htmlFor={htmlFor} className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-600">
            {label} {required && <span className="text-amber-dark">*</span>}
        </label>
        {children}
    </div>
);

export default function Contact() {
    const [form, setForm] = useState(initialForm);
    const set = (key) => (e) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((f) => ({ ...f, [key]: value }));
    };

    const buildBody = () =>
        [
            `Full Name: ${form.fullName}`,
            `Company Name: ${form.company}`,
            `Email Address: ${form.email}`,
            `Phone/WhatsApp: ${form.phone}`,
            `Country: ${form.country}`,
            `Destination Port: ${form.port}`,
            `Product Interest: ${form.product}`,
            `Grade Required: ${form.grade || "Standard export grade"}`,
            `Quantity (MT): ${form.quantity}`,
            `Shipment Terms: ${form.terms}`,
            `Target Month: ${form.targetMonth || "Flexible"}`,
            `COA/Specification Test Details Requested: ${form.coa ? "Yes" : "No"}`,
            "",
            "Additional Message/Specifications:",
            form.message || "—",
        ].join("\n");

    const subject = `Export Inquiry — ${form.product} — ${form.company || form.fullName}`;

    const handleMailto = (e) => {
        e.preventDefault();
        trackEvent("generate_lead", { method: "mailto", page: "contact" });
        window.location.href = `mailto:${COMPANY.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(buildBody())}`;
    };

    const handleWhatsApp = () => {
        trackEvent("whatsapp_click", { button_location: "contact-form", page: "contact" });
        const text = `Hello Axora PS Global LLP, I would like to request an export quote.\n\n${buildBody()}`;
        window.open(`https://wa.me/919602655086?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
    };

    return (
        <>
            <Seo
                title="Contact & Request a Quote | AXORA PS GLOBAL LLP"
                description="Request a rapeseed/mustard DOC export quote from AXORA PS GLOBAL LLP, Jaipur, India. Email sales@axorapsglobal.com or WhatsApp +91 9602655086. Open Monday–Sunday, 10 AM–8 PM IST."
                path="/contact"
                ogImage="/images/og-contact.jpg"
            />

            <section data-testid="contact-hero" className="relative overflow-hidden bg-white pt-[72px]">
                <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-gradient-to-b from-steel-light/80 via-steel-light/30 to-white" />
                <div className="relative mx-auto w-full max-w-7xl px-5 pb-16 pt-14 sm:px-8 lg:px-12 lg:pb-20 lg:pt-20">
                    <Reveal>
                        <Overline>Contact</Overline>
                        <h1 className="mt-4 max-w-3xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-navy sm:text-5xl">
                            Contact & Quote Request
                        </h1>
                        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
                            Share your specification and destination port. A co-founder — not a sales queue — reviews
                            every export inquiry.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section data-testid="contact-content" className="border-t border-slate-200 bg-white py-20 lg:py-24">
                <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
                    <div className="lg:col-span-5">
                        <Reveal>
                            <h2 className="font-display text-2xl font-bold tracking-tight text-navy">Corporate Details</h2>
                        </Reveal>
                        <ul className="mt-8 space-y-6">
                            {[
                                {
                                    icon: MapPin,
                                    label: "Office Address",
                                    value: COMPANY.address,
                                    testid: "contact-address",
                                },
                                {
                                    icon: Mail,
                                    label: "Email",
                                    value: COMPANY.email,
                                    href: `mailto:${COMPANY.email}`,
                                    testid: "contact-email",
                                },
                                {
                                    icon: Phone,
                                    label: "Phone / WhatsApp — Puneet Aggarwal, Co-Founder",
                                    value: COMPANY.phone,
                                    href: "tel:+919602655086",
                                    testid: "contact-phone-puneet",
                                },
                                {
                                    icon: Phone,
                                    label: "Phone / WhatsApp — Sushant Lohia, Co-Founder",
                                    value: "+91 7340211436",
                                    href: "tel:+917340211436",
                                    testid: "contact-phone-sushant",
                                },
                                {
                                    icon: Clock,
                                    label: "Business Hours",
                                    value: COMPANY.hours,
                                    testid: "contact-hours",
                                },
                            ].map((row, i) => (
                                <Reveal key={row.label} delay={i * 0.08}>
                                    <li className="flex items-start gap-4" data-testid={row.testid}>
                                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-steel-light">
                                            <row.icon className="h-5 w-5 text-navy" />
                                        </span>
                                        <div>
                                            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{row.label}</p>
                                            {row.href ? (
                                                <a href={row.href} className="mt-1 block text-sm font-medium text-navy transition-colors hover:text-steel">
                                                    {row.value}
                                                </a>
                                            ) : (
                                                <p className="mt-1 text-sm font-medium leading-relaxed text-navy">{row.value}</p>
                                            )}
                                        </div>
                                    </li>
                                </Reveal>
                            ))}
                        </ul>
                        <Reveal delay={0.2} className="mt-10 rounded-2xl bg-navy p-7">
                            <p className="font-display text-lg font-bold tracking-tight text-white">Fastest response: WhatsApp</p>
                            <p className="mt-2 text-sm leading-relaxed text-white/70">
                                For current pricing, COA copies, and loading slots, message us directly — typical
                                response within business hours.
                            </p>
                            <div className="mt-5 flex flex-col gap-3 sm:flex-row lg:flex-col xl:flex-row">
                                <WhatsAppButton label="WhatsApp Puneet" location="contact-sidebar-puneet" />
                                <WhatsAppButton label="WhatsApp Sushant" location="contact-sidebar-sushant" variant="white" href={WHATSAPP_LINK_SUSHANT} />
                            </div>
                        </Reveal>
                    </div>

                    <Reveal delay={0.1} className="lg:col-span-7">
                        <form
                            data-testid="quote-form"
                            onSubmit={handleMailto}
                            className="rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_20px_60px_rgba(10,37,64,0.08)] sm:p-10"
                        >
                            <h2 className="font-display text-2xl font-bold tracking-tight text-navy">Request an Export Quote</h2>
                            <p className="mt-2 text-sm leading-relaxed text-slate-600">
                                Fill in your requirement below. Submitting opens your email app with the inquiry
                                pre-filled — nothing is stored or sent to any server.
                            </p>
                            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
                                <Field label="Full Name" htmlFor="fullName" required>
                                    <input id="fullName" data-testid="form-full-name" required value={form.fullName} onChange={set("fullName")} className={inputClass} placeholder="Your full name" />
                                </Field>
                                <Field label="Company Name" htmlFor="company">
                                    <input id="company" data-testid="form-company-name" value={form.company} onChange={set("company")} className={inputClass} placeholder="Your company" />
                                </Field>
                                <Field label="Email Address" htmlFor="email" required>
                                    <input id="email" type="email" data-testid="form-email" required value={form.email} onChange={set("email")} className={inputClass} placeholder="you@company.com" />
                                </Field>
                                <Field label="Phone / WhatsApp" htmlFor="phone">
                                    <input id="phone" type="tel" data-testid="form-phone" value={form.phone} onChange={set("phone")} className={inputClass} placeholder="+xx xxxxxxxxxx" />
                                </Field>
                                <Field label="Country" htmlFor="country">
                                    <input id="country" data-testid="form-country" value={form.country} onChange={set("country")} className={inputClass} placeholder="Destination country" />
                                </Field>
                                <Field label="Destination Port" htmlFor="port">
                                    <input id="port" data-testid="form-destination-port" value={form.port} onChange={set("port")} className={inputClass} placeholder="e.g. Haiphong, Chattogram" />
                                </Field>
                                <Field label="Product Interest" htmlFor="product">
                                    <select id="product" data-testid="form-product-interest" value={form.product} onChange={set("product")} className={inputClass}>
                                        {PRODUCT_OPTIONS.map((o) => (
                                            <option key={o}>{o}</option>
                                        ))}
                                    </select>
                                </Field>
                                <Field label="Grade Required" htmlFor="grade">
                                    <input id="grade" data-testid="form-grade" value={form.grade} onChange={set("grade")} className={inputClass} placeholder="e.g. 38% protein min" />
                                </Field>
                                <Field label="Quantity (MT)" htmlFor="quantity">
                                    <input id="quantity" type="number" min="1" data-testid="form-quantity" value={form.quantity} onChange={set("quantity")} className={inputClass} placeholder="e.g. 100" />
                                </Field>
                                <Field label="Shipment Terms" htmlFor="terms">
                                    <select id="terms" data-testid="form-shipment-terms" value={form.terms} onChange={set("terms")} className={inputClass}>
                                        {TERMS_OPTIONS.map((o) => (
                                            <option key={o}>{o}</option>
                                        ))}
                                    </select>
                                </Field>
                                <Field label="Target Month" htmlFor="targetMonth">
                                    <input id="targetMonth" type="month" data-testid="form-target-month" value={form.targetMonth} onChange={set("targetMonth")} className={inputClass} />
                                </Field>
                                <div className="sm:col-span-2">
                                    <Field label="Additional Message / Specifications" htmlFor="message">
                                        <textarea id="message" rows={4} data-testid="form-message" value={form.message} onChange={set("message")} className={inputClass} placeholder="Anything else we should know — packaging, inspection, documentation…" />
                                    </Field>
                                </div>
                                <div className="space-y-3 sm:col-span-2">
                                    <label htmlFor="coa" className="flex cursor-pointer items-start gap-3 text-sm text-slate-700">
                                        <input id="coa" type="checkbox" data-testid="form-coa-checkbox" checked={form.coa} onChange={set("coa")} className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-[#0A2540]" />
                                        I would like COA/specification test details
                                    </label>
                                    <label htmlFor="consent" className="flex cursor-pointer items-start gap-3 text-sm text-slate-700">
                                        <input id="consent" type="checkbox" data-testid="form-consent-checkbox" required checked={form.consent} onChange={set("consent")} className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-[#0A2540]" />
                                        I agree to be contacted regarding this export inquiry <span className="text-amber-dark">*</span>
                                    </label>
                                </div>
                            </div>
                            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                                <button
                                    type="submit"
                                    data-testid="form-submit-mailto"
                                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-navy px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-navy-hover"
                                >
                                    <Send className="h-4 w-4" />
                                    Send Inquiry via Email
                                </button>
                                <button
                                    type="button"
                                    data-testid="form-submit-whatsapp"
                                    onClick={handleWhatsApp}
                                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-amber px-6 py-3.5 text-sm font-semibold text-navy-deep shadow-[0_10px_30px_rgba(245,166,35,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-amber-dark"
                                >
                                    <MessageCircle className="h-4 w-4" />
                                    Send via WhatsApp Instead
                                </button>
                            </div>
                            <p className="mt-4 text-xs leading-relaxed text-slate-500">
                                Both options open on your own device — your details go straight to {COMPANY.email} or
                                our WhatsApp, and nothing is stored by this website.
                            </p>
                        </form>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
